import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { accessDecision, loginReturnPath, type AccessLevel, type LoginReturnState, type User } from "@domain/Auth";
import { DEFAULT_PAGE } from "@domain/constants";
import { UserContext } from "@gui/contexts/user/UserContext";
import { browserTokenStore } from "@gui/infrastructure/pass-through/storage";

// Lazy: only a signed-in user without the role ever sees it, so it stays off first paint
// (Web/perf-critical-path.json). The routed area's PageErrorBoundary supplies the Suspense.
const NotAuthorized = React.lazy(() =>
	import("@gui/pages/NotAuthorized/NotAuthorized").then((m) => ({ default: m.NotAuthorized })),
);

/**
 * The route guard every registry entry renders through. The decision itself is the pure
 * `accessDecision` (`@domain/Auth`); this only turns it into a redirect or a view:
 *  - no stored session on a page that needs one → Login, which brings them back here afterwards;
 *  - signed in on Login → on to where they were going (or Home);
 *  - signed in without the role → the shared Not-Authorized view.
 */
export function RequireRole({ access, children }: React.PropsWithChildren<{ access: AccessLevel }>) {
	const user = React.useContext(UserContext) as User | undefined;
	const location = useLocation();

	switch (accessDecision(access, user, !!browserTokenStore.getJwt())) {
		case "allow":
			return children;
		case "signIn": {
			const state: LoginReturnState = { from: `${location.pathname}${location.search}` };
			return <Navigate to="/login" replace state={state} />;
		}
		case "leave":
			return <Navigate to={loginReturnPath(location.state) ?? DEFAULT_PAGE} replace />;
		case "deny":
			return <NotAuthorized />;
	}
}
